// gameData.ts
//
// Everything the sheet used to assert about World of Warcraft, fetched from the
// database instead. GET /raids/:id/sheet-config returns the game's ability
// catalogue (game_composition_tag) and one row per spec carrying the tags it
// provides, the resource it spends and what its output scales with.
//
// This exists because the sheet is going to have to be right about two game
// versions at once. Classic+ is coming, a single hardcoded class table cannot
// describe both, and every one of these facts is already stored per game_id.
//
// The unit is the SPEC, never the class, and that is the whole argument:
// every warrior has Pummel but only a feral druid has Feral Charge; every
// paladin has Kings but only a protection paladin has Sanctuary. A class table
// cannot express either, which is how a holy paladin ended up being offered
// Sanctuary in the first draft of the blessing grid.

import { BossPlan, RoleKey, Raider, canonClass } from "./assignmentModel";
import { NightGroup, NightRole } from "./nightRoles";
import { BlessingDef } from "./blessings";

export type TagInfo = {
  tag_key: string;
  tag_label: string;
  tag_category: string | null;
  tag_subcategory: string | null;
  description: string | null;
};

export type SpecInfo = {
  label: string;
  classKey: string | null;
  roleKey: string | null;
  tags: string[];
  resource: string | null;        // mana | rage | energy
  scalesWith: string[];           // attack_power | spell_power
};

// The night's encounters and every catalogue behind them, all resolved
// server-side from sheet_box / sheet_layout / sheet_night_role /
// sheet_blessing with the guild's own rows layered over the built-in ones.
export type SheetConfig = {
  gameId: string;
  tags: TagInfo[];
  specs: Record<string, SpecInfo>;
  // Insertion-ordered by the game's own class sort order, which is the order
  // the blessing grid's columns read in.
  classes: Record<string, { label: string; specKeys: string[] }>;
  plan: BossPlan[];
  instances: { raid_option_id: number; label: string | null }[];
  nightGroups: NightGroup[];
  nightRoles: NightRole[];
  blessings: BlessingDef[];
};

export const EMPTY_CONFIG: SheetConfig = {
  gameId: "", tags: [], specs: {}, classes: {},
  plan: [], instances: [], nightGroups: [], nightRoles: [], blessings: [],
};

// Class labels in the game's own order, for anything that lays out one column
// per class.
export const classOrder = (config: SheetConfig): string[] =>
  Object.keys(config.classes).map((k) => config.classes[k].label);

// role_key in the spec table is the game's vocabulary; RoleKey is the sheet's.
const ROLE_FROM_KEY: Record<string, RoleKey> = {
  tank: "tank", healer: "healer",
  melee_dps: "melee", ranged_dps: "ranged",
};

export function classKeyFor(config: SheetConfig, className: string): string | null {
  const want = canonClass(className).toLowerCase();
  const keys = Object.keys(config.classes);
  for (let i = 0; i < keys.length; i++) {
    if (keys[i] === want) return keys[i];
    if ((config.classes[keys[i]].label || "").toLowerCase() === want) return keys[i];
  }
  return null;
}

export type Traits = {
  specKey: string | null;
  specLabel: string | null;
  tags: string[];
  resource: string | null;
  scalesWith: string[];
  // True when the spec was not recorded and this had to be narrowed down from
  // the class and the seat they are filling. The blessing grid says so rather
  // than presenting a guess as fact.
  inferred: boolean;
};

const intersect = (lists: string[][]): string[] => {
  if (lists.length === 0) return [];
  return lists[0].filter((t) => lists.every((l) => l.indexOf(t) >= 0));
};

// What this raider can actually do.
//
// The recorded spec wins, but only if it belongs to their class: there are
// live characters whose metadata says a paladin is warrior_fury, and trusting
// that would hand a paladin's blessings to nobody. When the spec is missing or
// wrong, narrow by class and by the seat they are in, then take the
// INTERSECTION of what is left, so an unknown druid healer is credited with
// what every druid healer has and nothing more.
export function traitsFor(
  config: SheetConfig,
  className: string,
  role: RoleKey,
  specKey?: string | null,
): Traits {
  const classKey = classKeyFor(config, className);
  const direct = specKey ? config.specs[specKey] : null;
  if (direct && (!classKey || direct.classKey === classKey)) {
    return {
      specKey: specKey || null, specLabel: direct.label,
      tags: direct.tags, resource: direct.resource,
      scalesWith: direct.scalesWith, inferred: false,
    };
  }

  const all = classKey && config.classes[classKey]
    ? config.classes[classKey].specKeys.map((k) => config.specs[k]).filter(Boolean)
    : [];
  if (all.length === 0) {
    return {
      specKey: null, specLabel: null, tags: [],
      resource: null, scalesWith: [], inferred: true,
    };
  }
  const byRole = all.filter((s) => s.roleKey && ROLE_FROM_KEY[s.roleKey] === role);
  const pool = byRole.length > 0 ? byRole : all;

  return {
    specKey: null,
    specLabel: pool.length === 1 ? pool[0].label : null,
    tags: intersect(pool.map((s) => s.tags)),
    // Resource is a property of the class in practice, so agreement is the
    // normal case; disagreement means we genuinely do not know.
    resource: pool.every((s) => s.resource === pool[0].resource) ? pool[0].resource : null,
    scalesWith: intersect(pool.map((s) => s.scalesWith)),
    inferred: true,
  };
}

// Fold the traits onto the roster once, at load, so every eligibility check
// downstream is a plain array lookup instead of a resolution.
export function withTraits(roster: Raider[], config: SheetConfig): Raider[] {
  return roster.map((r) => {
    const t = traitsFor(config, r.className, r.role, r.specKey);
    return {
      ...r,
      specLabel: r.specLabel || t.specLabel,
      tags: t.tags,
      resource: t.resource,
      scalesWith: t.scalesWith,
      traitsInferred: t.inferred,
    };
  });
}

export const tagLabel = (config: SheetConfig, key: string): string => {
  const t = config.tags.filter((x) => x.tag_key === key)[0];
  return t ? t.tag_label : key;
};
